import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  Users, 
  Calendar, 
  MapPin, 
  Heart, 
  Brain,
  Shield,
  Award,
  Activity,
  Dna,
  UserCheck,
  TrendingUp,
  Clock,
  BarChart3,
  Lightbulb
} from 'lucide-react';

const About = () => {
  const timeline = [
    {
      year: '2024-2029',
      title: 'Phase 1: Pilot Study',
      description: 'Aim to recruit 2,000 participants at University of Waterloo with annual assessments for 5 years'
    },
    {
      year: '2025-2030',
      title: 'Phase 2: Expansion',
      description: 'Scale to 3 sites (Waterloo, Manitoba, Newfoundland) targeting 10,000 total participants'
    },
    {
      year: '2030-2035',
      title: 'Phase 3: Full Scale',
      description: 'Target recruitment of 20,000 participants across all sites'
    },
    {
      year: '2024-2044',
      title: 'Long-term Follow-up',
      description: '20 years of health monitoring through electronic health records'
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Free Health Assessment',
      description: 'Comprehensive health evaluation including advanced MRI and blood tests'
    },
    {
      icon: Brain,
      title: 'Cognitive Testing',
      description: 'Detailed assessment of memory, attention, and mental health'
    },
    {
      icon: Shield,
      title: 'Privacy Protection',
      description: 'Your data is protected with the highest security standards'
    },
    {
      icon: Award,
      title: 'Contribute to Science',
      description: 'Help advance medical research and improve healthcare for future generations'
    }
  ];

  return (
    <div className="about">
      {/* Hero Section */}
       <section className="hero" style={{ 
         paddingTop: '120px',
         backgroundImage: 'linear-gradient(135deg, rgba(30, 64, 175, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%), url(/images/young-adults.jpg)',
         backgroundSize: 'cover',
         backgroundPosition: 'center',
         backgroundAttachment: 'fixed',
         color: 'white'
       }}>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>About the Canadian Biobank</h1>
            <p>
              A state-of-the-art MRI-based longitudinal study designed to track and mitigate 
              health risks among young adult Canadians, focusing on cancer, mental health disorders, 
              Type 2 diabetes, and post-viral syndromes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Study Overview */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Study Overview</h2>
            <p className="section-subtitle">
              Understanding the comprehensive scope and significance of our groundbreaking research initiative.
            </p>
            
            <div className="overview-grid" style={{ marginTop: '3rem' }}>
              {/* Our Mission Card */}
              <motion.div 
                className="overview-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="overview-icon">
                  <Target size={32} />
                </div>
                <h3>Our Mission</h3>
                <p>
                  Create the most comprehensive dataset on young adult health in Canada. 
                  Aim to follow 20,000 participants aged 18-29 over 20 years.
                </p>
                <div className="overview-highlight">
                  <TrendingUp size={20} />
                  <span>3-phase approach tracking health predictors</span>
                </div>
              </motion.div>

              {/* Why Young Adults Card */}
              <motion.div 
                className="overview-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="overview-icon">
                  <Users size={32} />
                </div>
                <h3>Why Young Adults?</h3>
                <p>
                  Critical period for health development where lifestyle habits are established. 
                  Early detection leads to better outcomes.
                </p>
                <div className="overview-highlight">
                  <Lightbulb size={20} />
                  <span>Lifelong health patterns formed during this time</span>
                </div>
              </motion.div>

              {/* Study Impact Card */}
              <motion.div 
                className="overview-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="overview-icon">
                  <BarChart3 size={32} />
              </div>
                <h3>Research Impact</h3>
                <p>
                  Identify risk and protective factors to prevent serious conditions 
                  before they develop.
                </p>
                <div className="overview-highlight">
                  <Shield size={20} />
                  <span>AI-powered prevention strategies</span>
                </div>
              </motion.div>

              {/* Study Scope Card */}
              <motion.div 
                className="overview-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="overview-icon">
                  <Clock size={32} />
                </div>
                <h3>Study Scope</h3>
                <p>
                  Address 30-year trend of increasing chronic illness diagnoses using 
                  advanced MRI imaging and biospecimen collection.
                </p>
                <div className="overview-highlight">
                  <Activity size={20} />
                  <span>State-of-the-art technology</span>
              </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research Design */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Research Design & Methodology</h2>
            <p className="section-subtitle">
              Our comprehensive approach combines advanced imaging, biospecimen collection, 
              and behavioral assessments to understand the complex relationships between 
              lifestyle, biology, and health outcomes.
            </p>
            
            <div className="methodology-grid" style={{ marginTop: '3rem' }}>
              {/* Predictors We Track */}
              <motion.div 
                className="methodology-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="methodology-icon">
                  <Activity size={32} />
                </div>
                <h3>Predictors We Track</h3>
                <div className="methodology-items">
                  <div className="methodology-item">
                    <div className="item-header">
                      <UserCheck size={20} />
                      <span>Lifestyle Behaviors</span>
                    </div>
                    <p>Eating patterns, exercise, alcohol consumption, media use</p>
                  </div>
                  <div className="methodology-item">
                    <div className="item-header">
                      <Shield size={20} />
                      <span>Infection Load</span>
                    </div>
                    <p>PCR-confirmed SARS-CoV-2, detailed infection history</p>
                  </div>
                  <div className="methodology-item">
                    <div className="item-header">
                      <Users size={20} />
                      <span>Social Conditions</span>
                    </div>
                    <p>Isolation, support networks, resources</p>
                  </div>
                </div>
              </motion.div>

              {/* Moderating Factors */}
              <motion.div 
                className="methodology-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="methodology-icon">
                  <Dna size={32} />
                </div>
                <h3>Moderating Factors</h3>
                <div className="methodology-items">
                  <div className="methodology-item">
                    <div className="item-header">
                      <Dna size={20} />
                      <span>Genetic/Biological</span>
                    </div>
                    <p>TP53, DISC1, ACE2, TCF7L2 genes</p>
                  </div>
                  <div className="methodology-item">
                    <div className="item-header">
                      <Target size={20} />
                      <span>Demographic/Socioeconomic</span>
                    </div>
                    <p>Sex, family income, occupation, education</p>
                  </div>
              </div>
              </motion.div>

              {/* Health Outcomes */}
              <motion.div 
                className="methodology-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="methodology-icon">
                  <Heart size={32} />
                </div>
                <h3>Health Outcomes</h3>
                <div className="methodology-items">
                  <div className="methodology-item">
                    <div className="item-header">
                      <Target size={20} />
                      <span>Cancer</span>
                    </div>
                    <p>Early onset colorectal, pancreatic, kidney, thyroid</p>
                  </div>
                  <div className="methodology-item">
                    <div className="item-header">
                      <Activity size={20} />
                      <span>Type 2 Diabetes</span>
                    </div>
                    <p>Early detection and prevention</p>
                  </div>
                  <div className="methodology-item">
                    <div className="item-header">
                      <Brain size={20} />
                      <span>Stroke</span>
                    </div>
                    <p>Risk factors and prevention</p>
                  </div>
                  <div className="methodology-item">
                    <div className="item-header">
                      <Brain size={20} />
                      <span>Neuropsychiatric Disorders</span>
                    </div>
                    <p>Anxiety, depression, psychosis</p>
              </div>
                </div>
              </motion.div>

            </div>
          </motion.div>
        </div>
      </section>

      {/* Study Design */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Study Design</h2>
            <div className="grid grid-4" style={{ marginTop: '2rem' }}>
              <div className="text-center">
                <Target size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3>Target Population</h3>
                <p>Young adults aged 18-29 from diverse backgrounds across Canada</p>
              </div>
              <div className="text-center">
                <Users size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3>Target Sample Size</h3>
                <p>Goal of 20,000 participants across 5 study sites nationwide</p>
              </div>
              <div className="text-center">
                <Calendar size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3>Duration</h3>
                <p>15 years of follow-up through electronic health records</p>
              </div>
              <div className="text-center">
                <MapPin size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                <h3>Study Sites</h3>
                <p>Waterloo, Manitoba, Newfoundland, and Toronto locations</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Study Phases Diagram */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title">3 Study Phases</h2>
            <p className="section-subtitle">Overview of predictors, moderators, mediators, and disease incidence across phases.</p>
            <img
              src="/images/phase-diagram.png"
              alt="Study phases diagram: predictors, moderators, mediators, and disease incidence"
              style={{
                maxWidth: '100%',
                height: 'auto',
                marginTop: '2rem',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.08)'
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Study Timeline</h2>
            <div style={{ marginTop: '3rem' }}>
              {timeline.map((item, index) => (
                <motion.div 
                  key={item.year}
                  className="timeline-item"
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '3rem',
                    flexDirection: index % 2 === 0 ? 'row' : 'row-reverse'
                  }}
                >
                  <div style={{ 
                    flex: 1, 
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    padding: index % 2 === 0 ? '0 2rem 0 0' : '0 0 0 2rem'
                  }}>
                    <h3 style={{ color: '#1E40AF', marginBottom: '0.5rem' }}>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    flexShrink: 0
                  }}>
                    {item.year}
                  </div>
                  <div style={{ flex: 1 }}></div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Benefits of Participation</h2>
            <div className="grid grid-4" style={{ marginTop: '2rem' }}>
              {benefits.map((benefit, index) => (
                <motion.div 
                  key={benefit.title}
                  className="card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <benefit.icon size={32} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
                  <h3 style={{ marginBottom: '0.5rem' }}>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>


      {/* Funding */}
      {/* <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title">Funding & Support</h2>
            <p className="section-subtitle">
              The Canadian Biobank is supported by a $12M grant application 
              submitted last month for the Waterloo site (Phase 1). Additional funding 
              will be sought for Phase 2 and 3 expansion to Manitoba and Newfoundland sites.
            </p>
            <div className="grid grid-3" style={{ marginTop: '2rem' }}>
              <div className="card text-center">
                <h3 style={{ color: '#1E40AF' }}>University of Waterloo</h3>
                <p>Lead institution and primary study site</p>
              </div>
              <div className="card text-center">
                <h3 style={{ color: '#1E40AF' }}>Government Funding</h3>
                <p>Federal and provincial research grants</p>
              </div>
              <div className="card text-center">
                <h3 style={{ color: '#1E40AF' }}>Partner Institutions</h3>
                <p>Collaborative research network across Canada</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default About;
